import {
  HubMemberCurrencies,
  IntroducingBrokerMember,
} from "@letsbit/malcolmlb";
import Table, { ColumnsType } from "antd/es/table";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { messages } from "./messages";
import { Link } from "react-router-dom";
import { Input } from "@letsbit/milkylb";
import { useGetMembers } from "hooks/partnerApi/useGetMembers";
import { useCurrencies } from "hooks/user/useCurrencies";
import { useMemo } from "react";
import { useFormatter } from "hooks/utils/useFormatter";
import { ReactComponent as BalancesIcon } from "assets/images/icons/navbar/solar_pie.svg";
import { ReactComponent as DepositIcon } from "assets/images/icons/receive.svg";

import "./styles.scss";
import BigNumber from "bignumber.js";
import { Tooltip } from "antd";

export const IntroducingBrokerMembersTable: React.FC = () => {
  const { formatMessage } = useIntl();
  const { formatValue } = useFormatter();
  const { data: currencies } = useCurrencies();

  const currenciesObj = useMemo(() => {
    return currencies?.reduce(
      (prev: Record<string, HubMemberCurrencies>, currency) => {
        return {
          ...prev,
          [currency.id]: currency,
        };
      },
      {},
    );
  }, [currencies]);

  const { control, watch } = useForm<{
    inputFilterEmail: string;
  }>();

  const {
    data: members,
    isLoading,
    setSize,
  } = useGetMembers({
    email: watch("inputFilterEmail"),
  });

  const columns: ColumnsType<IntroducingBrokerMember> = [
    {
      title: (
        <span className="text-xs font-bold">
          <FormattedMessage {...messages.columnState} />
        </span>
      ),
      dataIndex: "state",
      key: "state",
      render: (_, { state, level }) => {
        return state === "active" ? (
          level > 2 ? (
            <span className="text-sm">Activo</span>
          ) : (
            <span className="text-sm opacity-50">Pendiente</span>
          )
        ) : (
          <span className="text-sm opacity-50">{state}</span>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-bold">
          <FormattedMessage {...messages.columnFullName} />
        </span>
      ),
      dataIndex: "naturalPersonData",
      key: "naturalPersonData",
      render: (_, { natural_person_data, juridical_person_data, email }) => {
        return (
          <div className="flex flex-col  text-sm">
            <span className="font-semibold">
              {natural_person_data
                ? `${natural_person_data?.first_name} ${natural_person_data?.last_name}`
                : juridical_person_data?.company_name}
            </span>
            <span>{email}</span>
          </div>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-bold">
          <FormattedMessage {...messages.columnTotal} />
        </span>
      ),
      dataIndex: "total_balance",
      key: "total_balance",
      render: (
        _,
        {
          uid,
          global_market_balance,
          local_market_balance,
          platform_balance,
          platform_currency,
        },
      ) => {
        return (
          <Link to={`/logged/members/${uid}/`}>
            <span className="text-sm">
              {global_market_balance &&
              platform_balance &&
              local_market_balance &&
              platform_currency
                ? `${formatValue(BigNumber.sum(global_market_balance, local_market_balance, platform_balance).toString(), currenciesObj?.[platform_currency]?.precision)} ${currenciesObj?.[platform_currency]?.ticker_id.toUpperCase() || platform_currency.toUpperCase()}`
                : "-"}
            </span>
          </Link>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-bold">
          <FormattedMessage {...messages.columnPlatform} />
        </span>
      ),
      dataIndex: "platform_balance",
      key: "platform_balance",
      render: (_, { uid, platform_balance, platform_currency }) => {
        return (
          <Link to={`/logged/members/${uid}/platform/balance`}>
            <span className="text-sm">
              {platform_balance && platform_currency
                ? `${formatValue(platform_balance, currenciesObj?.[platform_currency]?.precision)} ${currenciesObj?.[platform_currency]?.ticker_id.toUpperCase() || platform_currency.toUpperCase()}`
                : "-"}
            </span>
          </Link>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-bold">
          <FormattedMessage {...messages.columnLocalMarket} />
        </span>
      ),
      dataIndex: "local_market_balance",
      key: "local_market_balance",
      render: (
        _,
        {
          uid,
          local_market_balance,
          local_market_currency,
          local_market_account_active,
        },
      ) => {
        return local_market_account_active ? (
          <Link to={`/logged/members/${uid}/local-market/balance`}>
            <span className="text-sm">
              {local_market_balance && local_market_currency
                ? `${formatValue(local_market_balance, currenciesObj?.[local_market_currency]?.precision)} ${currenciesObj?.[local_market_currency]?.ticker_id.toUpperCase() || local_market_currency.toUpperCase()}`
                : "-"}
            </span>
          </Link>
        ) : (
          <span className="text-sm opacity-50">
            <FormattedMessage {...messages.columnNotCreated} />
          </span>
        );
      },
    },
    {
      title: (
        <span className=" text-xs font-bold">
          <FormattedMessage {...messages.columnGlobalMarket} />
        </span>
      ),
      dataIndex: "global_market_balance",
      key: "global_market_balance",
      render: (
        _,
        {
          uid,
          global_market_balance,
          global_market_currency,
          global_market_account_active,
        },
      ) => {
        return global_market_account_active ? (
          <Link to={`/logged/members/${uid}/global-market/balance`}>
            <span className="text-sm">
              {global_market_balance && global_market_currency
                ? `${formatValue(global_market_balance, currenciesObj?.[global_market_currency]?.precision)} ${currenciesObj?.[global_market_currency]?.ticker_id.toUpperCase() || global_market_currency.toUpperCase()}`
                : "-"}
            </span>
          </Link>
        ) : (
          <span className="text-sm opacity-50">
            <FormattedMessage {...messages.columnNotCreated} />
          </span>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-bold">
          <FormattedMessage {...messages.columnLinks} />
        </span>
      ),
      dataIndex: "uid",
      key: "links",
      render: (_, { uid }) => {
        return (
          <div className="flex flex-row gap-x-4  text-sm">
            <Tooltip title={formatMessage(messages.linkBalances)}>
              <Link to={`/logged/members/${uid}`}>
                <BalancesIcon width={18} height={18} />
              </Link>
            </Tooltip>
            <Tooltip title={formatMessage(messages.linkDepositAddresses)}>
              <Link
                to={`/logged/members/${uid}/platform/deposit-address/ars/ar_cvu`}
              >
                <DepositIcon width={18} height={18} />
              </Link>
            </Tooltip>
          </div>
        );
      },
    },
  ];
  return (
    <div className="introducing-broker-members-table mr-2 flex w-full grow flex-col overflow-auto">
      <div className="flex flex-col">
        <h4 className="flex w-full justify-between text-lg text-[--color-neutral-0]">
          <FormattedMessage {...messages.searchBy} />
        </h4>
        <div className="flex w-full gap-x-4">
          <Controller
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                placeholder={formatMessage(messages.inputFilterEmail)}
                value={value}
                onIonInput={onChange}
                error={!!error}
                message={error?.message}
                style={{ width: 413 }}
              />
            )}
            name="inputFilterEmail"
          />
        </div>
      </div>
      <div className="mb-[150px] flex flex-col gap-2 p-2 ">
        <Table
          dataSource={members?.flat() || []}
          columns={columns}
          loading={isLoading}
          pagination={{
            onChange: (page) => {
              setSize(page);
            },
          }}
        />
      </div>
    </div>
  );
};
